<template>
  <div class="ctc-create-page">
    <ctc-pending-verifications
      v-if="pendingVerifications.length"
      :verifications="pendingVerifications"
      :abandoning-ids="abandoningVerificationIds"
      @resume="$emit('resume-verification', $event)"
      @abandon="$emit('abandon-verification', $event)"
    />
    <section
      v-if="activeVerification"
      class="ctc-active-verification"
      aria-label="Actions de la vérification en cours"
    >
      <span class="ctc-active-verification__copy">
        <strong>Vérification en cours</strong>
        <small>{{ activeVerificationTitle }}</small>
      </span>
      <span class="ctc-active-verification__actions">
        <rmr-action-button
          v-if="resumedVerification"
          label="Quitter"
          variant="transparent"
          :color="colors.indigo"
          size="xs"
          :disabled="activeVerificationAbandoning || state.launching"
          @click="$emit('leave-verification')"
        />
        <rmr-action-button
          label="Abandonner"
          variant="transparent"
          :color="colors.danger"
          size="xs"
          :loading="activeVerificationAbandoning"
          :disabled="activeVerificationAbandoning || state.launching"
          @click="activeAbandonConfirmOpen = true"
        />
      </span>
    </section>
    <v-card ref="cardRef" flat class="ctc-create">
      <rmr-stepper :value="state.step" :steps="steps">
        <v-stepper-content :step="1" data-step="1">
          <ctc-source-step
            :source-url="state.sourceUrl"
            :sites="sites"
            :site-id="state.siteId"
            :url-error="urlError || ''"
            :can-analyze="canAnalyze"
            :analyzing="state.analyzing"
            :analysis-error="state.analysisError"
            :product="state.product"
            :product-errors="productErrors"
            :offer-check="state.offerCheck"
            :checking-offer="state.checkingOffer"
            :offer-check-error="state.offerCheckError || ''"
            :from-opportunity="state.origin === 'opportunity'"
            @update:sourceUrl="flow.setSourceUrl"
            @select-site="flow.selectSite"
            @analyze="flow.analyze"
            @retry-offer-check="flow.retryOfferCheck"
            @update-product-field="flow.updateProductField"
            @change-product="$emit('reset')"
          />
        </v-stepper-content>

        <v-stepper-content :step="2" data-step="2">
          <ctc-affilizz-step
            v-if="state.product"
            :site="selectedSite"
            :product="state.product"
            :widget-enabled="state.widgetEnabled"
            :widget-type="state.widgetType"
            :similar="state.similar"
            :similar-actions="flow.similarActions"
            :offer-check="state.offerCheck"
            :checking-offer="state.checkingOffer"
            :offer-check-error="state.offerCheckError"
            :verification="state.verification"
            :affiliation="affiliation"
            :verification-stale="verificationStale"
            :can-verify="canVerify"
            @retry-offer-check="flow.retryOfferCheck"
            @update:widgetEnabled="flow.setWidgetEnabled"
            @update:widgetType="flow.setWidgetType"
            @verify="flow.verifyAffilizz"
            @recheck="flow.recheckAffiliation"
            @disable-widget="flow.disableVerificationWidget"
            @choose-product="flow.goToStep(1)"
          />
        </v-stepper-content>

        <v-stepper-content :step="3" data-step="3">
          <ctc-settings-step
            v-if="state.product"
            :site="selectedSite"
            :editorial-format="state.editorialFormat"
            :editorial-format-error="editorialFormatError || ''"
            :signature="state.signature"
            :signature-error="signatureError || ''"
            :post-status="state.postStatus"
            :scheduled-date="state.scheduledDate"
            :scheduled-time="state.scheduledTime"
            :schedule-error="scheduleError || ''"
            @edit-site="flow.goToStep(1)"
            @update:editorialFormat="flow.setEditorialFormat"
            @update:signature="flow.setSignature"
            @update:postStatus="flow.setPostStatus"
            @update:schedule="flow.setSchedule"
          />
        </v-stepper-content>

        <v-stepper-content :step="4" data-step="4">
          <ctc-launch-step
            v-if="state.product"
            :product="state.product"
            :site="selectedSite"
            :signature="state.signature"
            :editorial-format="state.editorialFormat"
            :post-status="state.postStatus"
            :scheduled-at="scheduledAt"
            :widget-enabled="state.widgetEnabled"
            :widget-type="state.widgetType"
            :similar-products="state.similar.selected"
            :affiliation="affiliation"
            :blocker="launchBlocker"
            :rejected="!!state.launchError"
            @edit-step="flow.goToStep"
          />
        </v-stepper-content>
      </rmr-stepper>

      <div class="ctc-create__footer">
        <rmr-notice
          v-if="state.launchError"
          variant="error"
          :text="state.launchError"
          role="alert"
          class="ctc-create__launch-error"
        />
        <rmr-notice
          v-if="state.cancelError"
          variant="error"
          :text="state.cancelError"
          role="alert"
          class="ctc-create__launch-error"
        />
        <div v-if="nextBlockedReason" class="ctc-create__reason" role="status">
          <v-icon small :color="mutedColor">{{
            icons.mdiInformationOutline
          }}</v-icon>
          {{ nextBlockedReason }}
        </div>
        <div class="ctc-create__actions">
          <rmr-action-button
            v-if="!activeVerification"
            label="Réinitialiser"
            :icon="icons.mdiRestore"
            variant="ghost"
            size="sm"
            :disabled="!canReset"
            class="ctc-create__reset"
            @click="$emit('reset')"
          />
          <rmr-action-button
            label="Précédent"
            :icon="icons.mdiArrowLeft"
            variant="ghost"
            :disabled="state.step === 1 || state.launching"
            @click="flow.previous"
          />
          <rmr-action-button
            v-if="state.step < steps.length"
            label="Suivant"
            :icon="icons.mdiArrowRight"
            :disabled="!canGoNext"
            class="ctc-create__primary"
            @click="flow.next"
          />
          <rmr-action-button
            v-else
            label="Lancer la génération"
            :icon="icons.mdiRocketLaunchOutline"
            :loading="state.launching"
            :disabled="!canLaunch"
            class="ctc-create__primary"
            @click="$emit('launch')"
          />
        </div>
      </div>
    </v-card>

    <rmr-confirm-dialog
      v-model="activeAbandonConfirmOpen"
      danger
      title="Abandonner cette vérification ?"
      :message="activeAbandonMessage"
      confirm-label="Abandonner"
      cancel-label="Conserver"
      @confirm="confirmActiveAbandon"
    />

    <ctc-launch-progress-modal :value="state.launching" />
  </div>
</template>

<script>
import { computed, nextTick, ref, watch } from "@vue/composition-api";
import {
  mdiArrowLeft,
  mdiArrowRight,
  mdiInformationOutline,
  mdiRestore,
  mdiRocketLaunchOutline,
} from "@mdi/js";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrStepper from "@/components/ui/layout/RmrStepper.vue";
import { RMR_DANGER, RMR_INDIGO, RMR_TEXT_MUTED } from "@/styles/tokens";

import { CREATE_STEPS } from "../constants";
import CtcAffilizzStep from "./CtcAffilizzStep.vue";
import CtcLaunchProgressModal from "./CtcLaunchProgressModal.vue";
import CtcLaunchStep from "./CtcLaunchStep.vue";
import CtcPendingVerifications from "./CtcPendingVerifications.vue";
import CtcSettingsStep from "./CtcSettingsStep.vue";
import CtcSourceStep from "./CtcSourceStep.vue";

export default {
  name: "CtcCreateView",
  components: {
    CtcAffilizzStep,
    CtcLaunchProgressModal,
    CtcLaunchStep,
    CtcPendingVerifications,
    CtcSettingsStep,
    CtcSourceStep,
    RmrActionButton,
    RmrConfirmDialog,
    RmrNotice,
    RmrStepper,
  },
  props: {
    // useCtcCreateFlow() instance, owned by the workspace
    flow: {
      type: Object,
      required: true,
    },
    sites: {
      type: Array,
      default: () => [],
    },
    // The manager's Affilizz verifications not finalized (resumable).
    pendingVerifications: {
      type: Array,
      default: () => [],
    },
    abandoningVerificationIds: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const cardRef = ref(null);
    const { state } = props.flow;
    const activeAbandonConfirmOpen = ref(false);

    // Refs nested in a prop object are not unwrapped by the template.
    const unwrap = (key) => computed(() => props.flow[key].value);
    const activeVerification = computed(() => {
      const { article, requestId } = state.verification;
      return article && requestId ? article : null;
    });

    // On a step change, keep the new step in view when the previous one was
    // long (mobile) and move the focus to its title for keyboard and screen
    // reader users.
    watch(
      () => state.step,
      async (step) => {
        await nextTick();
        const el = cardRef.value && cardRef.value.$el;
        if (!el) return;
        if (el.getBoundingClientRect().top < 0) {
          const reduced =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
          el.scrollIntoView({
            behavior: reduced ? "auto" : "smooth",
            block: "start",
          });
        }
        const title = el.querySelector(
          `[data-step="${step}"] .ctc-step__title`
        );
        if (title) {
          title.setAttribute("tabindex", "-1");
          title.focus({ preventScroll: true });
        }
      }
    );

    return {
      activeAbandonConfirmOpen,
      activeAbandonMessage: computed(() =>
        activeVerification.value
          ? `« ${
              activeVerification.value.productTitle ||
              (state.product && state.product.title) ||
              "Cette création"
            } » ne pourra plus être reprise. Sa vérification Affilizz sera annulée.`
          : ""
      ),
      activeVerification,
      activeVerificationAbandoning: computed(
        () =>
          !!activeVerification.value &&
          props.abandoningVerificationIds.includes(activeVerification.value.id)
      ),
      activeVerificationTitle: computed(
        () =>
          (activeVerification.value &&
            (activeVerification.value.productTitle ||
              (activeVerification.value.product &&
                activeVerification.value.product.title))) ||
          (state.product && state.product.title) ||
          "Création sans titre"
      ),
      affiliation: unwrap("affiliation"),
      canAnalyze: unwrap("canAnalyze"),
      canVerify: unwrap("canVerify"),
      canGoNext: unwrap("canGoNext"),
      canLaunch: unwrap("canLaunch"),
      canReset: computed(
        () =>
          !state.launching &&
          (!!state.sourceUrl ||
            !!state.product ||
            !!state.siteId ||
            state.step > 1)
      ),
      cardRef,
      colors: { danger: RMR_DANGER, indigo: RMR_INDIGO },
      confirmActiveAbandon: () => {
        if (activeVerification.value) {
          emit("abandon-verification", {
            ...activeVerification.value,
            requestId: state.verification.requestId,
            productTitle:
              activeVerification.value.productTitle ||
              (state.product && state.product.title) ||
              "Cette création",
          });
        }
        activeAbandonConfirmOpen.value = false;
      },
      icons: {
        mdiArrowLeft,
        mdiArrowRight,
        mdiInformationOutline,
        mdiRestore,
        mdiRocketLaunchOutline,
      },
      mutedColor: RMR_TEXT_MUTED,
      editorialFormatError: unwrap("editorialFormatError"),
      launchBlocker: unwrap("launchBlocker"),
      nextBlockedReason: unwrap("nextBlockedReason"),
      productErrors: unwrap("productErrors"),
      signatureError: unwrap("signatureError"),
      scheduleError: unwrap("scheduleError"),
      scheduledAt: unwrap("scheduledAt"),
      selectedSite: computed(
        () => props.sites.find(({ id }) => id === state.siteId) || null
      ),
      state,
      steps: CREATE_STEPS,
      resumedVerification: computed(
        () => !!activeVerification.value && state.verification.resumed
      ),
      urlError: unwrap("urlError"),
      verificationStale: unwrap("verificationStale"),
    };
  },
};
</script>

<style lang="scss" scoped>
.ctc-create-page {
  display: grid;
  gap: 20px;
}

// clip, not hidden: rounded corners without a scroll container, so sticky
// parts of a step (the widget panel) follow the page scroll.
.ctc-create {
  border: 1px solid $rmr-border-hover !important;
  border-radius: $rmr-radius-2xl !important;
  overflow: clip;
}

.ctc-active-verification {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px 10px 16px;
  border: 1px solid $rmr-ca-tint;
  border-radius: $rmr-radius-lg;
  background: $rmr-ca-soft;
}

.ctc-active-verification__copy {
  display: grid;
  min-width: 0;
  gap: 2px;

  strong {
    color: $rmr-text-primary;
    font-size: $rmr-fs-base;
  }

  small {
    overflow: hidden;
    color: $rmr-text-muted;
    font-size: $rmr-fs-sm;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.ctc-active-verification__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 6px;
}

// RmrStepper is laid out for height-constrained modals: in a page, let the
// content follow the document flow.
.ctc-create ::v-deep .rmr-stepper,
.ctc-create ::v-deep .rmr-stepper .v-stepper__items,
.ctc-create ::v-deep .v-stepper__content,
.ctc-create ::v-deep .v-stepper__wrapper {
  overflow: visible;
}

.ctc-create ::v-deep .v-stepper__content {
  padding: 24px;
}

.ctc-create__footer {
  padding: 14px 24px;
  border-top: 1px solid $rmr-border-hover;
  background: $rmr-surface-2;
}

.ctc-create__launch-error {
  margin-bottom: 12px;
}

.ctc-create__reason {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: $rmr-fs-sm;
  color: $rmr-text-muted;
}

.ctc-create__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.ctc-create__reset {
  margin-right: auto;
}

@media (max-width: 599px) {
  .ctc-active-verification {
    align-items: flex-start;
    flex-direction: column;
  }

  .ctc-active-verification__actions {
    width: 100%;
  }

  .ctc-create ::v-deep .v-stepper__content {
    padding: 16px 12px;
  }

  .ctc-create__footer {
    padding: 12px;
  }

  // Full-width primary action on top, secondary actions share the row below.
  .ctc-create__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .ctc-create__primary {
    grid-column: 1 / -1;
    order: -1;
  }

  .ctc-create__reset {
    margin-right: 0;
  }
}

@media (min-width: 600px) and (max-width: 1100px) {
}
</style>
